import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api, { getApiError } from '../api/client';
import Logo from '../components/Logo';
import FormField from '../components/ui/FormField';
import { useAuthStore } from '../store/authStore';
import { useTenantStore } from '../store/tenantStore';
import { useCompanyPath, useCompanySlug } from '../utils/routing';
import { usePermissionStore } from '../store/permissionStore';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [companyInfo, setCompanyInfo] = useState<{ name: string } | null>(null);
  const { login } = useAuthStore();
  const { setCompany } = useTenantStore();
  const navigate = useNavigate();
  const location = useLocation();
  const companySlug = useCompanySlug();
  const locationPath = useCompanyPath('/location');
  const fromPath = (location.state as { from?: string })?.from;

  useEffect(() => {
    setCompany(companySlug);
    api.get(`/tenants/by-slug/${companySlug}`)
      .then(r => setCompanyInfo(r.data))
      .catch(() => setCompanyInfo(null));
  }, [companySlug, setCompany]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (loading) return;
    if (!email.trim() || !password) {
      toast.error('Email and password are required');
      return;
    }
    setLoading(true); setError('');
    try {
      const response = await api.post('/auth/login', {
        email: email.trim().toLowerCase(),
        password,
        tenant_slug: companySlug,
      });
      login(response.data.access_token, response.data.user);
      const slug = response.data.user.tenant_slug || companySlug;
      setCompany(slug, response.data.user.tenant_name);
      await usePermissionStore.getState().load();
      toast.success(`Welcome, ${response.data.user.full_name}!`);
      navigate(fromPath || locationPath, { replace: true });
    } catch (requestError: unknown) {
      const msg = getApiError(requestError, 'Sign-in failed. Check your credentials.');
      setError(msg);
      toast.error(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex bg-surface">
      <div className="hidden lg:flex lg:w-1/2 gradient-bg flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="absolute rounded-full bg-white" style={{
              width: (i % 4) * 38 + 64, height: (i % 4) * 38 + 64, top: `${(i * 23) % 100}%`, left: `${(i * 37) % 100}%`, opacity: 0.16,
            }} />
          ))}
        </div>
        <div className="relative"><Logo size="lg" /></div>
        <div className="relative text-white">
          <h1 className="text-4xl font-bold mb-4 leading-tight">Complete ERP for<br/>Growing Businesses</h1>
          <p className="text-primary-100 text-lg mb-8">Inventory, dealer distribution, live deliveries and every core operation in one platform.</p>
          <div className="grid grid-cols-2 gap-4">
            {['CRM & Sales', 'Inventory Intelligence', 'Dealer Network', 'Live Tracking', 'Finance & Accounts', 'Projects & HR'].map(feature => (
              <div key={feature} className="flex items-center gap-2 text-indigo-50">
                <span className="w-5 h-5 bg-white/20 rounded-full text-center text-xs">✓</span>
                <span className="text-sm">{feature}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="relative text-primary-100 text-sm">© {new Date().getFullYear()} SMEPRO360</div>
      </div>
      <div className="flex-1 flex items-center justify-center p-8 bg-surface-card">
        <div className="w-full max-w-md">
          <div className="mb-8 lg:hidden"><Logo size="md" /></div>
          {companyInfo ? (
            <div className="mb-4 px-3 py-2 rounded-xl bg-primary-50 border border-primary-100 text-sm text-primary-700">
              Signing in to <strong>{companyInfo.name}</strong>
            </div>
          ) : (
            <div className="mb-4 px-3 py-2 rounded-xl bg-amber-50 border border-amber-100 text-sm text-amber-700">
              Company <strong>{companySlug}</strong> — verify your workspace URL
            </div>
          )}
          <h2 className="text-3xl font-bold text-content mb-2">Welcome back</h2>
          <p className="text-content-secondary mb-8">Sign in to your SMEPRO360 workspace.</p>
          {error && <div role="alert" className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-5">
            <FormField label="Email address" required>
              <input type="email" autoComplete="username" value={email} onChange={e => setEmail(e.target.value)}
                className="input-field" placeholder="name@company.com" required autoFocus />
            </FormField>
            <FormField label="Password" required>
              <div className="relative">
                <input type={showPassword ? 'text' : 'password'} autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)}
                  className="input-field pr-20" placeholder="Enter your password" required />
                <button type="button" onClick={() => setShowPassword(v => !v)}
                  className="absolute inset-y-0 right-3 text-xs font-semibold text-primary-600">{showPassword ? 'Hide' : 'View'}</button>
              </div>
            </FormField>
            <div className="flex justify-between text-sm">
              <Link to="/" className="text-content-secondary hover:text-primary-600">Back to home</Link>
              <span className="text-content-muted">Need help? Contact admin</span>
            </div>
            <button type="submit" disabled={loading} className="w-full btn-primary py-3">
              {loading ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Signing in…</> : 'Sign in'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};
export default Login;
